import React, { useState } from 'react'
import { useApiContext } from '../../components/App/AppApi'
import { eqxAppConfigApi } from '../interfaces/eqxAppConfig'
import eqxApi from './eqxApi'
import eqxEventBroker from './eqxEventBroker'

export function useApi(api: eqxApi | null = null) {
    let apiContext = null
    apiContext = useApiContext()
    if (api) return api
    if (apiContext) {
        return apiContext
    }
    return new eqxApi({} as eqxApiProps)
}
export function useFetch(url: string, params: any = {}, config: any = {}) {
    const [data, setData] = useState<any>()
    const [error, setError] = useState<string>()
    const [loading, setLoading] = useState<boolean>(true)
    const api = useApi(config?.api)

    React.useEffect(() => {
        api.request(url, config?.method ?? 'get', params, config)
            .then((response: any) => {
                setData(response)
                setLoading(false)
            })
            .catch((err: any) => {
                const str = typeof err == 'object' ? err.error ?? err.message ?? 'An error occurred' : String(err)
                setError(str)
                setLoading(false)
            })
    }, [])

    return { data, error, loading, setData }
}
export interface eqxApiProps {
    config: eqxAppConfigApi
    token?: string
    /** v2 tenant URL segment only, e.g. `/_MyOrg-001` — not app routes like `/continuum-bi`. */
    tenantUrlPrefix?: string
}
class eqxApiInstance {
    events: eqxEventBroker
    token?: string | null
    config: eqxAppConfigApi
    baseUrl: string
    tenantUrlPrefix: string
    endpoints: any

    constructor(props: eqxApiProps) {
        this.config = props.config
        this.token = props.token
        this.baseUrl = props.config?.baseUrl
        this.tenantUrlPrefix = props.tenantUrlPrefix ?? ''
        this.events = new eqxEventBroker(this)
    }

    buildRequestUrl(path: string): string {
        const base = this.baseUrl.replace(/\/$/, '')
        const tenant =
            this.tenantUrlPrefix === ''
                ? ''
                : this.tenantUrlPrefix.startsWith('/')
                  ? this.tenantUrlPrefix
                  : `/${this.tenantUrlPrefix}`
        const suffix = path.startsWith('/') ? path : `/${path}`
        return `${base}${tenant}${suffix}`
    }

    /**
     * Overrideable method to add custom headers to requests
     * @param headers The headers object to modify
     * @param url The request URL
     * @param method The request method
     */
    setCustomHeaders(headers: Record<string, string>, url: string, method: string): void {
        // Override this method in subclasses to add custom headers
    }
    request(url: string, method: string = 'get', data?: any, configOptions?: any, token?: string) {
        let config = configOptions || {}
        config.method = method
        if (!config.headers) config.headers = {}
        if (!token) token = this.token ?? undefined
        if (token && !configOptions?.noAuth) {
            config.credentials = 'include' //needed to pass CORs
            config.headers.authorization = `Bearer ${token}`
        }

        // Add custom headers
        this.setCustomHeaders(config.headers, url, method)
        if (config.method == 'post') {
            //check if data is an instance of FormData
            if (data instanceof FormData) {
                config.body = data
            } else {
                config.body = JSON.stringify(data)
                config.headers['Content-type'] = 'application/json'
            }
        }
        url = this.buildRequestUrl(url)
        return fetch(url, config).then(async (response) => {
            if (!response) throw new Error('No response')
            if (response.status != 200) {
                if (response.status == 401) {
                    const event = this.events.trigger('response401', [response])
                    if (!event.isPreventDefault) {
                        let responseData = null
                        try {
                            responseData = await response.json()
                        } catch (err) {
                            return Promise.reject({})
                        }
                        return Promise.reject(responseData)
                    } else {
                        return Promise.resolve({})
                    }
                } else if (response.status == 426) {
                    const event = this.events.trigger('response426', [response])
                    if (!event.isPreventDefault) {
                        let responseData = null
                        try {
                            responseData = await response.json()
                        } catch (err) {
                            return Promise.reject({ status: 426, message: 'Undergoing Maintenance' })
                        }
                        return Promise.reject(responseData)
                    } else {
                        return Promise.resolve({})
                    }
                } else {
                    let responseData = null
                    try {
                        responseData = await response.json()
                    } catch (err) {
                        return Promise.reject({})
                    }
                    return Promise.reject(responseData)
                }
            }
            return response.json()
        })
    }
    get(url: string, data?: any, config?: any, token?: string) {
        return this.request(url, 'get', data, config, token)
    }
    post(url: string, data?: any, config?: any, token?: string) {
        return this.request(url, 'post', data, config, token)
    }
    postFormData(url: string, data?: FormData, config?: any, token?: string) {
        return this.request(url, 'post', data, config, token)
    }
}
export default eqxApiInstance
